import React, { memo } from "react";
import { Box } from "@mui/material";
import {
  BRAND,
  GREY,
  BASE,
  BORDER,
  SUCCESS,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import IconComponent from "../../../../../components/Icon/IconComponent";
import CircularLoader from "../../../../../components/Loaders/CircularLoader";
import { Button } from "../../../../../components/Buttons";
import { sharedBillingStyles as s, SDR_MEETING_LINK, formatDate } from "./billingUtils";
import BillingPlanCard from "./BillingPlanCard";
import BillingSdrCard from "./BillingSdrCard";

const planStyles = {
  contentWrapper: {
    padding: "24px 24px 28px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  switcherWrap: {
    display: "inline-flex",
    background: BASE.BACKGROUND_LIGHT,
    padding: "5px",
    borderRadius: "999px",
    border: `1px solid ${BORDER.DEFAULT}`,
    gap: "5px",
    boxShadow: `0 12px 20px -26px ${GREY.SECONDARY}`,
  },
  switcherPill: {
    height: "36px",
    padding: "0 20px",
    borderRadius: "999px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${BASE.TRANSPARENT}`,
    transition:
      "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
  },
  billingToggleWrap: {
    display: "flex",
    background: BASE.BACKGROUND_LIGHT,
    padding: "5px",
    borderRadius: "999px",
    border: `1px solid ${BORDER.DEFAULT}`,
    width: "fit-content",
    boxShadow: `0 12px 20px -26px ${GREY.SECONDARY}`,
  },
  billingTogglePill: {
    minHeight: "34px",
    padding: "0 16px",
    borderRadius: "999px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${BASE.TRANSPARENT}`,
    transition:
      "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
  },
};

/**
 * BillingPlansSection
 *
 * Hosts the "Just the Tools" / "Done for You" segment switcher and
 * renders the appropriate plan cards based on the active view.
 */
const BillingPlansSection = memo(
  ({
    packages,
    loading,
    billingCycle,
    setBillingCycle,
    planView,
    setPlanView,
    currentSubscription,
    selectedPlanId,
    setSelectedPlanId,
    buyingPlanId,
    changingPlanId,
    handleBuyPackage,
    handleChangePlan,
    requestPlanChange,
    cancelScheduledChange,
    isCancellingScheduledChange,
    takenOnceFreePlan,
  }) => {
    const hasActiveSub = ["active", "trialing"].includes(currentSubscription?.status);
    // A plan card no longer commits on click — it asks the hook to classify the change and open a
    // confirmation modal (upgrade = immediate, downgrade = period-end, buy = checkout) so an
    // accidental click can never change a plan. Falls back to direct routing if not wired.
    const onSelectPlan = (plan) => {
      if (typeof requestPlanChange === "function") {
        requestPlanChange(plan);
        return;
      }
      const isThisCurrent =
        currentSubscription?.plan?.price_id === plan?.stripe_price_id &&
        currentSubscription?.status === "active";
      if (isThisCurrent) return; // already on this plan — no-op
      if (hasActiveSub && !plan?.isTopUp && typeof handleChangePlan === "function") {
        handleChangePlan(plan);
      } else {
        handleBuyPackage(plan);
      }
    };
    const busyPlanId = changingPlanId || buyingPlanId;
    if (loading) {
      return (
        <Box style={s.loadingContainer}>
          <CircularLoader size={32} />
        </Box>
      );
    }

    if (packages.length === 0) {
      return (
        <Box style={s.emptyContainer}>
          <TypographyInline
            size="md"
            color={GREY.SECONDARY}
            label="No packages found. Please check back later."
          />
        </Box>
      );
    }

    const toolPackages = packages.filter(
      (p) =>
        !p.isSdr &&
        !p.isFree &&
        p.interval === billingCycle &&
        !/reachiq\s+growth\s+annual/i.test(p.name || "") &&
        // Never surface the internal $1 QA test plan to real tenants in the change-plan grid.
        // (It is signup-only, unlocked via a gated control on the registration plan screen.)
        (p.name || "").trim().toLowerCase() !== "qa dollar test",
    );

    const sdrPlan = packages.find((p) => p.isSdr);
    const isSdrCurrent =
      sdrPlan &&
      currentSubscription &&
      currentSubscription.plan?.price_id === sdrPlan.stripe_price_id &&
      currentSubscription.status === "active";

    return (
      <Box style={planStyles.contentWrapper}>
        {/* ── Pending scheduled plan change banner (downgrade takes effect at period end) ── */}
        {currentSubscription?.pending_plan && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "12px 16px",
              background: PRIMARY.W_50,
              border: `1px solid ${PRIMARY.W_200}`,
              borderRadius: "10px",
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconComponent
                iconLabel={ICON_LABELS.SCHEDULE}
                style={{ color: BRAND.PRIMARY, fontSize: "20px" }}
              />
              <TypographyInline
                size="xs"
                fontWeight={fontWeight.MEDIUM}
                color={GREY.PRIMARY}
                label={`Scheduled: your plan switches to ${currentSubscription.pending_plan.plan_name || "a new plan"}${currentSubscription.plan_change_effective_at ? " on " + formatDate(currentSubscription.plan_change_effective_at) : " at your next renewal"}. You keep your current plan until then.`}
              />
            </Box>
            <Box style={{ width: "fit-content", flexShrink: 0 }}>
              <Button
                label="Cancel change"
                variant="ghost"
                size="sm36"
                onClick={cancelScheduledChange}
                isLoading={isCancellingScheduledChange}
                disabled={isCancellingScheduledChange}
              />
            </Box>
          </Box>
        )}

        {/* ── Segment switcher ── */}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Box style={planStyles.switcherWrap}>
            <Box
              onClick={() => setPlanView("tools")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setPlanView("tools");
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={planView === "tools"}
              style={{
                ...planStyles.switcherPill,
                background:
                  planView === "tools" ? BASE.WHITE : BASE.TRANSPARENT,
                boxShadow:
                  planView === "tools"
                    ? `0 12px 22px -22px ${GREY.SECONDARY}`
                    : "none",
                border:
                  planView === "tools"
                    ? `1px solid ${BRAND.PRIMARY}`
                    : `1px solid ${BASE.TRANSPARENT}`,
              }}
              sx={{
                "&:hover": {
                  background:
                    planView === "tools" ? BASE.WHITE : BORDER.DISABLED,
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${BRAND.PRIMARY}`,
                  outlineOffset: "-1px",
                },
              }}
            >
              <TypographyInline
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={planView === "tools" ? BRAND.PRIMARY : GREY.TERTIARY}
                label="Just the Tools"
              />
            </Box>
            <Box
              onClick={() => setPlanView("sdr")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setPlanView("sdr");
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={planView === "sdr"}
              style={{
                ...planStyles.switcherPill,
                background: planView === "sdr" ? BASE.WHITE : BASE.TRANSPARENT,
                boxShadow:
                  planView === "sdr"
                    ? `0 12px 22px -22px ${GREY.SECONDARY}`
                    : "none",
                border:
                  planView === "sdr"
                    ? `1px solid ${BRAND.PRIMARY}`
                    : `1px solid ${BASE.TRANSPARENT}`,
              }}
              sx={{
                "&:hover": {
                  background: planView === "sdr" ? BASE.WHITE : BORDER.DISABLED,
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${BRAND.PRIMARY}`,
                  outlineOffset: "-1px",
                },
              }}
            >
              <Box
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={planView === "sdr" ? BRAND.PRIMARY : GREY.TERTIARY}
                  label="Done for You"
                />
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    background:
                      planView === "sdr" ? PRIMARY.W_100 : BASE.WHITE,
                    color: BRAND.PRIMARY,
                    border: `1px solid ${PRIMARY.W_200}`,
                    padding: "2px 8px",
                    borderRadius: "999px",
                    letterSpacing: "0.2px",
                  }}
                >
                  NEW
                </span>
              </Box>
            </Box>
          </Box>
          <TypographyInline
            size="xs"
            color={GREY.SECONDARY}
            label={
              planView === "tools"
                ? "Self-serve tools billed monthly or yearly"
                : "Fully managed outbound, we handle everything"
            }
          />
        </Box>

        {/* ── Just the Tools ── */}
        {planView === "tools" && (
          <>
            {/* Billing cycle toggle */}
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
              }}
              sx={{
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                },
              }}
            >
              <TypographyInline
                size="xs"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.SECONDARY}
                label="Billing Cycle"
              />
              <Box style={planStyles.billingToggleWrap}>
                <Box
                  onClick={() => setBillingCycle("month")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setBillingCycle("month");
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={billingCycle === "month"}
                  style={{
                    ...planStyles.billingTogglePill,
                    background:
                      billingCycle === "month"
                        ? BASE.WHITE
                        : BASE.TRANSPARENT,
                    border:
                      billingCycle === "month"
                        ? `1px solid ${BRAND.PRIMARY}`
                        : `1px solid ${BASE.TRANSPARENT}`,
                    boxShadow:
                      billingCycle === "month"
                        ? `0 12px 22px -22px ${GREY.SECONDARY}`
                        : "none",
                  }}
                  sx={{
                    "&:hover": {
                      background:
                        billingCycle === "month" ? BASE.WHITE : BORDER.DISABLED,
                      transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${BRAND.PRIMARY}`,
                      outlineOffset: "-1px",
                    },
                  }}
                >
                  <TypographyInline
                    size="xs"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={billingCycle === "month" ? BRAND.PRIMARY : GREY.TERTIARY}
                    label="Monthly"
                  />
                </Box>
                <Box
                  onClick={() => setBillingCycle("year")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setBillingCycle("year");
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={billingCycle === "year"}
                  style={{
                    ...planStyles.billingTogglePill,
                    background:
                      billingCycle === "year"
                        ? BASE.WHITE
                        : BASE.TRANSPARENT,
                    border:
                      billingCycle === "year"
                        ? `1px solid ${BRAND.PRIMARY}`
                        : `1px solid ${BASE.TRANSPARENT}`,
                    boxShadow:
                      billingCycle === "year"
                        ? `0 12px 22px -22px ${GREY.SECONDARY}`
                        : "none",
                  }}
                  sx={{
                    "&:hover": {
                      background:
                        billingCycle === "year" ? BASE.WHITE : BORDER.DISABLED,
                      transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${BRAND.PRIMARY}`,
                      outlineOffset: "-1px",
                    },
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <TypographyInline
                      size="xs"
                      fontWeight={fontWeight.SEMIBOLD}
                      color={billingCycle === "year" ? BRAND.PRIMARY : GREY.TERTIARY}
                      label="Yearly"
                    />
                    <span
                      style={{
                        fontSize: "9px",
                        fontWeight: 700,
                        background:
                          billingCycle === "year"
                            ? PRIMARY.W_100
                            : SUCCESS.W_100,
                        color:
                          billingCycle === "year"
                            ? BRAND.PRIMARY
                            : SUCCESS.W_600,
                        border:
                          billingCycle === "year"
                            ? `1px solid ${PRIMARY.W_200}`
                            : `1px solid ${SUCCESS.W_200}`,
                        padding: "1px 6px",
                        borderRadius: "12px",
                        letterSpacing: "0.2px",
                      }}
                    >
                      SAVE 20%
                    </span>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Plan cards grid */}
            <Box
              style={{
                display: "grid",
                gap: "22px",
                width: "100%",
                padding: "0 0 4px",
              }}
              sx={{
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
              }}
            >
              {toolPackages.length === 0 ? (
                <Box
                  style={{
                    gridColumn: "1 / -1",
                    textAlign: "center",
                    padding: "40px",
                  }}
                >
                  <TypographyInline
                    size="md"
                    color={GREY.SECONDARY}
                    label="No packages found."
                  />
                </Box>
              ) : (
                toolPackages.map((plan) => {
                  const isCurrent =
                    currentSubscription &&
                    currentSubscription.plan?.price_id === plan.stripe_price_id &&
                    currentSubscription.status === "active";
                  const isSelected = selectedPlanId === plan.id;

                  return (
                    <BillingPlanCard
                      key={plan.id}
                      plan={plan}
                      isCurrent={isCurrent}
                      isSelected={isSelected}
                      billingCycle={billingCycle}
                      takenOnceFreePlan={takenOnceFreePlan}
                      buyingPlanId={busyPlanId}
                      hasActiveSub={hasActiveSub}
                      onSelect={() => setSelectedPlanId(plan.id)}
                      onBuy={onSelectPlan}
                    />
                  );
                })
              )}
            </Box>
          </>
        )}

        {/* ── Done for You ── */}
        {planView === "sdr" && (
          <Box style={{ padding: "4px 0 8px" }}>
            <BillingSdrCard
              sdrPlan={sdrPlan}
              isCurrent={isSdrCurrent}
              buyingPlanId={busyPlanId}
              hasActiveSub={hasActiveSub}
              onBuy={onSelectPlan}
            />
          </Box>
        )}

        {/* Enterprise CTA */}
        <Box
          style={{
            textAlign: "center",
            background: BASE.BACKGROUND_LIGHT,
            padding: "20px 22px",
            borderRadius: "14px",
            border: `1px dashed ${BORDER.DEFAULT}`,
            boxShadow: `0 14px 24px -26px ${GREY.SECONDARY}`,
          }}
        >
          <TypographyInline
            size="sm"
            fontWeight={fontWeight.SEMIBOLD}
            color={GREY.PRIMARY}
            label="Need an Enterprise solution?"
          />
          <Box style={{ marginTop: "6px" }}>
            <TypographyInline
              size="xs"
              color={GREY.TERTIARY}
              label="Custom credits, SSO, dedicated infrastructure, and premium compliance features."
            />
          </Box>
          <Box style={{ marginTop: "8px", display: "inline-block" }}>
            <Button
              label="Contact Sales Team"
              variant="ghost"
              size="sm36"
              onClick={() => window.open(SDR_MEETING_LINK, "_blank")}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);

BillingPlansSection.displayName = "BillingPlansSection";

export default BillingPlansSection;
